import { useFormik } from "formik";
import PropTypes from "prop-types";
import update from "immutability-helper";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import CurrencyInput from "react-currency-input-field";

import {
  backtestFilterInitialValues,
  backtestFilterValidationSchema,
} from "../validation";
import {
  backtestStrategy,
  // createCustomScenario,
  // deleteCustomScenario,
  getAllPredefinedScenarios,
  updatePredefinedScenario,
} from "features/Strategy/service";
import {
  sub,
  format,
  differenceInDays,
  MONTH_DATE_YEAR_FORMAT,
  unixSecToMillisec,
  // isBefore,
} from "utils/date";
import {
  setStartedBacktest,
  setBacktestLog,
  setIsBacktestComplete,
} from "features/Strategy/slice";

import { toast } from "Layout/slice";
import { Form as CTForm } from "components/Form";
import StartStrategy from "shared/StrategyControl/StartStrategy";
import { parseErrorMessage, isEmptyObj } from "utils";
import { BacktestFilterContainer } from "features/Strategy/v2/Detail/StrategyDetails.styles";
import { useCommunityStrategies } from "features/Strategy/hook/useCommunityStrategies";
import { useNavigation } from "utils/navigate";
import NewCustomDatePicker from "features/Strategy/v2/BacktestingTab/components/Datepicker/NewCustomDatePicker";
import { ButtonStyleContainer } from "shared/Modal/SnaptradeModal/SnaptradeModal.styles";
import { Tooltip } from "components/Tooltip";
import { HelpIcon } from "assets/icons";

export const BacktestFilter = ({
  runTest,
  setRunTest,
  strategyName,
  waitForStrategy,
  setIsLoading,
  start_date,
  end_date,
  canEditCode,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(true);
  // const [isLoadingCustomSce, setIsLoadingCustomSce] = useState(false);
  const [showSceneriosDetails, setShowSceneriosDetails] = useState({});
  const [predefinedScenarios, setPreDefinedScenarios] = useState([]);
  const [showSceneriosCount, setShowSceneriosCount] = useState(4);
  const [showBrokerModal, setShowBrokerModal] = useState(false);

  const { navigateToV2PortfolioDetail } = useNavigation();

  const { refetch: refetchActiveStrategies } = useCommunityStrategies({
    type: "active",
    enabled: false,
  });

  useEffect(() => {
    // Get all predefined scenarios
    (async () => {
      try {
        const response = await getAllPredefinedScenarios();
        setPreDefinedScenarios(response);
      } catch (error) {
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (runTest == 1) {
      let payload = formik.values;
      if (
        formik.values.quickSelect ||
        formik.values.start ||
        formik.values.end
      ) {
        payload = formik.values;
      } else {
        payload = {
          fees: "0",
          slippage: "0",
          end: new Date(),
          quickSelect: "1M",
          initialBalance: "100000",
          start: sub({ months: 1 }),
        };
        formik.values.quickSelect = payload.quickSelect;
        formik.values.start = payload.start;
        formik.values.end = payload.end;
      }
      handleSubmit(payload);
    } else if (runTest == 2) {
      toggleBrokerModal();
      setRunTest(0);
    }
  }, [runTest]);

  useEffect(() => {
    if (start_date && end_date) {
      formik.values.start = new Date(unixSecToMillisec(start_date));
      formik.values.end = new Date(unixSecToMillisec(end_date));
      const start = new Date(start_date);
      const end = new Date(end_date);
      const today = new Date();

      if (differenceInDays(end, today) == 0) {
        const diff = differenceInDays(end, start);
        formik.values.quickSelect = getQuickSelect(diff);
      }
    }
  }, [start_date, end_date]);

  const handleMoveCard = useCallback(
    async ({ dragIndex, ...dragProps }, { hoverIndex, ...hoverProps }) => {
      try {
        updatePredefinedScenario(dragProps.id, {
          order: hoverProps.order,
        });
        updatePredefinedScenario(hoverProps.id, {
          order: dragProps.order,
        });

        setPreDefinedScenarios((prevCards) =>
          update(prevCards, {
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, prevCards[dragIndex]],
            ],
          })
        );
      } catch (error) {
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
      }
    },
    []
  );

  const getQuickSelect = (diff) => {
    if (diff >= 365 && diff % 365 != 0) {
      return "";
    }
    if (diff <= 365) {
      return `${diff / 30}M`;
    } else if (diff <= 1825) {
      return `${diff / 365}Y`;
    }
  };

  const toggleBrokerModal = () => {
    setShowBrokerModal(!showBrokerModal);
  };

  const handleMoreClick = () => {
    if (showSceneriosCount === 4) {
      setShowSceneriosCount(predefinedScenarios.length);
    } else {
      setShowSceneriosCount(4);
    }
  };

  const handleSubmit = async (values) => {
    delete values.quickSelect;
    try {
      dispatch(setBacktestLog("> starting backtest..."));
      dispatch(setIsBacktestComplete(false));
      dispatch(setStartedBacktest(true));
      setIsLoading(true);
      const payload = {
        ...values,
        start: format(MONTH_DATE_YEAR_FORMAT, values.start),
        end: format(MONTH_DATE_YEAR_FORMAT, values.end),
      };
      let response = await backtestStrategy(id, "", payload);

      if (response && response.backtest_id) {
        waitForStrategy(response.backtest_id);
      }
    } catch (error) {
      const err = `ERROR: ${parseErrorMessage(error)}`;
      dispatch(setIsBacktestComplete(true));
      dispatch(setStartedBacktest(false));
      dispatch(setBacktestLog(err));
      dispatch(
        toast({
          type: "error",
          body: err,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleScenerioClick = (details) => {
    if (isEmptyObj(details)) {
      return;
    }
    formik.setFieldValue("start", new Date(unixSecToMillisec(details.start)));
    formik.setFieldValue("end", new Date(unixSecToMillisec(details.end)));
    formik.setFieldValue("quickSelect", "");
    formik.setFieldError("start", "");
    formik.setFieldError("end", "");
    setShowSceneriosDetails(details);
  };

  // const handleSaveCustomScenario = async () => {
  //   try {
  //     setIsLoadingCustomSce(true);
  //     const { name, description, start, end, fees, slippage, initialBalance } =
  //       formik.values;
  //     const payload = {
  //       name,
  //       fees: fees || "0",
  //       slippage: slippage || "0",
  //       tags: [],
  //       type: null,
  //       start: format(MONTH_DATE_YEAR_FORMAT, start),
  //       end: format(MONTH_DATE_YEAR_FORMAT, end),
  //       initial_balance_usd: initialBalance,
  //       description: description || "Custom Backtest Scenario",
  //     };
  //     const response = await createCustomScenario(payload);
  //     const allScenarios = predefinedScenarios;
  //     allScenarios.unshift(response);
  //     setPreDefinedScenarios(allScenarios);
  //     formik.setFieldValue("start", "");
  //     formik.setFieldValue("end", "");
  //     formik.setFieldValue("quickSelect", "");
  //     formik.setFieldValue("name", "");
  //     formik.setFieldValue("description", "");
  //     setShowSceneriosDetails({});
  //   } catch (error) {
  //     dispatch(
  //       toast({
  //         type: "error",
  //         body: parseErrorMessage(error),
  //       })
  //     );
  //   } finally {
  //     setIsLoadingCustomSce(false);
  //   }
  // };

  // const handleDeleteCustomScenario = async () => {
  //   try {
  //     if (
  //       isEmptyObj(showSceneriosDetails) ||
  //       !showSceneriosDetails ||
  //       !showSceneriosDetails.id
  //     ) {
  //       dispatch(
  //         toast({
  //           type: "error",
  //           body: "Scenario ID missing",
  //         })
  //       );
  //       return;
  //     }
  //     setIsLoadingCustomSce(true);
  //     const response = await deleteCustomScenario(showSceneriosDetails.id);
  //     dispatch(
  //       toast({
  //         body: response.message,
  //       })
  //     );
  //     const filtered = predefinedScenarios.filter(
  //       (d) => d.id != showSceneriosDetails.id
  //     );
  //     setPreDefinedScenarios(filtered);
  //   } catch (error) {
  //     dispatch(
  //       toast({
  //         type: "error",
  //         body: parseErrorMessage(error),
  //       })
  //     );
  //   } finally {
  //     setIsLoadingCustomSce(false);
  //   }
  // };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: backtestFilterInitialValues,
    onSubmit: handleSubmit,
    validationSchema: backtestFilterValidationSchema,
    // validateOnChange: false,
  });

  // const isSaveCustomScenario = () => {
  //   return (
  //     formik.values.start &&
  //     formik.values.end &&
  //     !formik.values.quickSelect &&
  //     isEmptyObj(showSceneriosDetails)
  //   );
  // };

  const onStartedStrategy = async (portfolioId) => {
    await refetchActiveStrategies();

    const options = {
      search: `?activeTab=strategyManagement`,
    };

    navigateToV2PortfolioDetail(portfolioId, options);
  };

  return (
    <BacktestFilterContainer>
      {/* Advance Filter */}
      <CTForm onSubmit={formik.handleSubmit} className={"form"}>
        {/* Date Range Picker */}
        <div className="calendar-group">
          <Form.Label className="input-form-label">Date Range</Form.Label>
          <NewCustomDatePicker
            formik={formik}
            onClick={handleScenerioClick}
            handleMoveCard={handleMoveCard}
            activeId={showSceneriosDetails.name}
            showSceneriosCount={showSceneriosCount}
            predefinedScenarios={predefinedScenarios}
            isLoading={isLoading}
            handleMoreClick={handleMoreClick}
          />
          {((formik.touched.start && formik.errors.start) ||
            (formik.touched.end && formik.errors.end)) && (
            <div className="form-error">
              {formik.errors.start || formik.errors.end}
            </div>
          )}
        </div>

        <div className="FormGroup">
          <Form.Label>
            Slippage %
            <Tooltip
              value={
                "Simulates the difference between expected and executed price. Enter a percentage to account for market impact/spread (e.g., 0.05%)."
              }
              placement="right"
              tooltipClassName="v2-tooltip"
            >
              <span className="tooltip-help">
                <HelpIcon />
              </span>
            </Tooltip>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Slippage"
            {...formik.getFieldProps("slippage")}
            className="form-control"
          />
          {formik.touched.slippage && formik.errors.slippage && (
            <div className="form-error">{formik.errors.slippage}</div>
          )}
        </div>

        <div className="FormGroup">
          <Form.Label className="input-form-label">
            Fees $
            <Tooltip
              value={
                "Per-order commission/transaction cost applied in the backtest (USD). Use the average fee charged by your broker."
              }
              placement="right"
              tooltipClassName="v2-tooltip"
            >
              <span className="tooltip-help">
                <HelpIcon />
              </span>
            </Tooltip>
          </Form.Label>
          <CurrencyInput
            id="fees"
            name="fees"
            placeholder="Fees"
            decimalsLimit={2}
            value={formik.values.fees}
            onValueChange={(value) => formik.setFieldValue("fees", value)}
            className={`form-control mb-0 ${
              formik.touched.fees && formik.errors.fees ? "is-invalid" : ""
            }`}
          />
          {formik.touched.fees && formik.errors.fees ? (
            <div className="form-error">{formik.errors.fees}</div>
          ) : null}
        </div>

        <div className="FormGroup">
          <Form.Label className="input-form-label">
            Initial Balance USD
          </Form.Label>
          <CurrencyInput
            id="initialBalance"
            name="initialBalance"
            placeholder="Initial Balance"
            prefix="$"
            groupSeparator=","
            decimalsLimit={0}
            value={formik.values.initialBalance}
            onValueChange={(value) =>
              formik.setFieldValue("initialBalance", value)
            }
            className={`form-control filter-control mb-0 ${
              formik.touched.initialBalance && formik.errors.initialBalance
                ? "is-invalid"
                : ""
            }`}
          />
          {formik.touched.initialBalance && formik.errors.initialBalance && (
            <div className="form-error">{formik.errors.initialBalance}</div>
          )}
        </div>

        <ButtonStyleContainer>
          <button
            className="btn backtest-btn"
            type="submit"
            disabled={
              !formik.values.start ||
              !formik.values.end ||
              // isLoadingCustomSce ||
              !canEditCode
            }
          >
            Run Backtest
          </button>
        </ButtonStyleContainer>
      </CTForm>

      {/* <span className="action-wrapper">
            {!isEmptyObj(showSceneriosDetails) &&
              showSceneriosDetails.is_custom && (
                <Button
                  size="small"
                  className="w-100 mt-3 buttonV2-outlined"
                  onClick={handleDeleteCustomScenario}
                  disabled={isLoadingCustomSce}
                >
                  Delete Scenario
                </Button>
              )}
            {isSaveCustomScenario() && (
              <Button
                size="small"
                className="w-100 mt-3"
                onClick={handleSaveCustomScenario}
                disabled={!formik.values.name || isLoadingCustomSce}
              >
                Save Scenario
              </Button>
            )}

            {isSaveCustomScenario() && (
              <div className="advanced">
                <Card>
                  <div className="divider"></div>
                  <Row>
                    <Col>
                      <Form.Control
                        type="text"
                        placeholder="Name"
                        {...formik.getFieldProps("name")}
                      />
                      {formik.touched.fees && formik.errors.fees ? (
                        <div className="form-error">{formik.errors.fees}</div>
                      ) : null}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Form.Control
                        type="text"
                        placeholder="Description"
                        {...formik.getFieldProps("description")}
                      />
                      {formik.touched.fees && formik.errors.fees ? (
                        <div className="form-error">{formik.errors.fees}</div>
                      ) : null}
                    </Col>
                  </Row>
                </Card>
              </div>
            )}
          </span> */}

      <StartStrategy
        showBrokerModal={showBrokerModal}
        toggleBrokerModal={toggleBrokerModal}
        strategyId={id}
        strategyName={strategyName}
        skipBrokerModal={true}
        onStartedStrategy={onStartedStrategy}
      />
    </BacktestFilterContainer>
  );
};

BacktestFilter.propTypes = {
  runTest: PropTypes.number,
  setRunTest: PropTypes.func,
  strategyName: PropTypes.string,
  waitForStrategy: PropTypes.func,
  setIsLoading: PropTypes.func,
  start_date: PropTypes.string,
  end_date: PropTypes.string,
  canEditCode: PropTypes.bool,
};
